import { FC } from "react";
import { ProductionState } from "config";
import { TaxIncentive } from "./types";
import classNames from "classnames";
import { Modal, ModalBody, ModalHeader } from "components/common/modal";
import { useTaxIncentiveCatalog } from "hooks/useBudget/useTaxIncentiveCatalog";
import {
  findJurisdictionByName,
  mapJurisdictionToCatalogDetail,
} from "./TaxIncentives/taxIncentiveCatalog.mapper";

interface AddDayBreakModalProps {
  open: boolean;
  setOpen: () => void;
  productionState?: ProductionState;
  taxIncentive?: TaxIncentive;
}

export const TaxIncentivesModal: FC<AddDayBreakModalProps> = ({
  open,
  setOpen,
  productionState,
  taxIncentive,
}) => {
  const { jurisdictions } = useTaxIncentiveCatalog({ enabled: open });
  const jurisdiction = productionState
    ? findJurisdictionByName(jurisdictions, productionState)
    : undefined;
  const catalogDetail = jurisdiction ? mapJurisdictionToCatalogDetail(jurisdiction) : undefined;

  const taxIncentives = catalogDetail
    ? {
        "Type of Incentive": catalogDetail.typeOfIncentive,
        "Application Website": catalogDetail.applicationWebsite,
        "Minimum Spend": catalogDetail.minimumSpend,
        "Annual Cap": catalogDetail.annualCap,
        "Project Cap": catalogDetail.projectCap,
        "Terms": catalogDetail.terms,
        "Additional Notes": catalogDetail.additionalNotes ?? taxIncentive?.incentives,
      }
    : { "Additional Notes": taxIncentive?.incentives };

  const tableInformation: { label: string; field: keyof typeof taxIncentives }[] = [
    {
      label: "Type of Incentive",
      field: "Type of Incentive",
    },
    { label: "Application Website", field: "Application Website" },
    { label: "Minimum Spend", field: "Minimum Spend" },
    { label: "Annual Cap", field: "Annual Cap" },
    { label: "Project Cap", field: "Project Cap" },
    { label: "Terms", field: "Terms" },
  ];

  return (
    <Modal isOpen={open} onClose={setOpen}>
      <ModalHeader
        title={`${productionState ?? taxIncentive?.location_name} Tax Incentives`}
        onClose={setOpen}
      />
      <ModalBody>
        <div className="flex flex-col gap-4">
          {tableInformation.map(({ label, field }) => {
            const value = taxIncentives[field];
            if (!value) return null;
            return (
              <div key={field} className="flex flex-col gap-1">
                <span className="text-regular-3 text-muted-foreground">{label}</span>
                {field === "Application Website" && typeof value === "string" ? (
                  <a
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classNames("text-regular-2 text-primary hover:underline")}
                  >
                    {value}
                  </a>
                ) : (
                  <span className="text-regular-2 text-foreground">{value}</span>
                )}
              </div>
            );
          })}
          {taxIncentives["Additional Notes"] ? (
            <div className="flex flex-col gap-1">
              <span className="text-regular-3 text-muted-foreground">Additional Notes</span>
              <span className="text-regular-2 text-foreground">
                {taxIncentives["Additional Notes"]}
              </span>
            </div>
          ) : null}
        </div>
      </ModalBody>
    </Modal>
  );
};
